import React, { useEffect, useMemo, useState } from 'react';
import userService from '../services/userService';
import { useT } from '../i18n/LanguageContext';

/**
 * + NEW USER, on the Users tab. One dialog, every role, and the result is a login that works.
 *
 * The form asks for exactly what decides whether the account works, and nothing else:
 *
 *   ROLE      -> what the app lets them do.
 *   CLINIC    -> clinic role only. A clinic login is bound to ONE practice, and the server refuses
 *                to create one without it - an unbound clinic login authenticates fine and then
 *                sees an empty app. So the field is required here, before the request is sent.
 *   CAN SEE   -> sales rep only. The practices whose orders, IVRs and invoices they see and the
 *                only ones they may submit for. A rep with none ticked sees only their own
 *                submissions (the Kevin Wood case), so the dialog warns before saving that way.
 *   EMAIL / PASSWORD -> the credentials. The password policy is the server's (12+ chars, upper,
 *                lower, digit, special) and is checked here first so the admin gets one clear
 *                message instead of a 400.
 *
 * There is no "save and add another": the Users list is right behind the dialog, and the new row
 * appears at the top of it with its access spelled out, which is the real confirmation.
 */

export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{12,}$/;

export const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', hint: 'Every screen: orders, IVRs, invoicing, statements, users.' },
  { value: 'sales_rep', label: 'Sales Rep', hint: 'Submits orders and IVRs for the clinics ticked below, and sees their invoices.' },
  { value: 'clinic', label: 'Clinic', hint: 'A practice login: submits orders and IVRs for its own clinic and sees its invoices.' },
];

const inputClass =
  'w-full h-[44px] px-3 border border-[#d6dce8] rounded-[8px] text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]';

/**
 * Which clinics to OFFER. The admin clinic list is the raw QuickBooks customer roster, most of
 * which is not a practice; an admin has already ruled those out (isPractice: false) and removed
 * some (isActive: false). Offering them here would create a login for a bank-feed row.
 */
export const offerableClinics = (clinics) =>
  (Array.isArray(clinics) ? clinics : [])
    .filter((c) => c && c._id && c.isActive !== false && c.isPractice !== false)
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

export function validateNewUser(form) {
  if (!form.firstName.trim() || !form.lastName.trim()) return 'First and last name are required.';
  if (!form.email.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'That email address does not look right.';
  if (!form.password) return 'Password is required.';
  if (form.password.length < 12) return 'Password must be at least 12 characters.';
  if (!PASSWORD_REGEX.test(form.password)) {
    return 'Password must include an uppercase letter, a lowercase letter, a number and a special character (@$!%*?&#).';
  }
  if (form.role === 'clinic' && !form.clinic) return 'Pick the clinic this login belongs to.';
  return '';
}

/** The request body for POST /admin/users - only the fields the chosen role carries. */
export function buildPayload(form) {
  const payload = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    password: form.password,
    role: form.role,
  };
  if (form.role === 'clinic') payload.clinic = form.clinic;
  if (form.role === 'sales_rep') payload.clinics = form.clinics;
  return payload;
}

const EMPTY = { firstName: '', lastName: '', role: 'sales_rep', clinic: '', clinics: [], email: '', password: '' };

export default function NewUserModal({ clinics, onClose, onCreated }) {
  const t = useT();
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmNoClinics, setConfirmNoClinics] = useState(false);

  const offered = useMemo(() => offerableClinics(clinics), [clinics]);
  const needle = filter.trim().toLowerCase();
  const shownClinics = needle
    ? offered.filter((c) => String(c.name || '').toLowerCase().includes(needle))
    : offered;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    setConfirmNoClinics(false);
  };

  const toggleClinic = (id) => {
    setForm((prev) => ({
      ...prev,
      clinics: prev.clinics.includes(id) ? prev.clinics.filter((c) => c !== id) : [...prev.clinics, id],
    }));
    setConfirmNoClinics(false);
  };

  const submit = async () => {
    const problem = validateNewUser(form);
    if (problem) {
      setError(problem);
      return;
    }
    // A rep with no clinics is a login that looks fine and sees nothing but their own work. Allowed
    // - an admin may tick the accounts later - but never by accident.
    if (form.role === 'sales_rep' && form.clinics.length === 0 && !confirmNoClinics) {
      setConfirmNoClinics(true);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await userService.createUser(buildPayload(form));
      const created = res?.data?.data || res?.data || {};
      onCreated({
        ...created,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        password: form.password,
      });
    } catch (err) {
      setError('Could not confirm whether the user was created. Refresh the user list before trying again.');
    } finally {
      setSaving(false);
    }
  };

  const roleHint = ROLE_OPTIONS.find((r) => r.value === form.role)?.hint;
  const star = <span className="text-[#f23e41]">*</span>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-label={t('New user')}>
      <div className="absolute inset-0 bg-black/60" onClick={saving ? undefined : onClose} />

      <div className="relative bg-white rounded-[14px] shadow-xl w-[640px] max-w-[calc(100vw-2rem)] z-10 max-h-[90vh] flex flex-col">
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-[16px] font-semibold text-[#0f172a]">{t('New user')}</h2>
            <p className="text-[12px] text-[#64748b] mt-0.5">
              {t('They can sign in the moment you save, with the email and password below.')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a] shrink-0"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>

        <form
          className="px-5 py-4 overflow-y-auto space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nu-first" className="block text-xs font-medium text-[#64748b] mb-1.5">
                {t('First name')} {star}
              </label>
              <input id="nu-first" type="text" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} className={inputClass} autoFocus />
            </div>
            <div>
              <label htmlFor="nu-last" className="block text-xs font-medium text-[#64748b] mb-1.5">
                {t('Last name')} {star}
              </label>
              <input id="nu-last" type="text" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} className={inputClass} />
            </div>
          </div>

          <div>
            <label htmlFor="nu-role" className="block text-xs font-medium text-[#64748b] mb-1.5">
              {t('Role')} {star}
            </label>
            <select id="nu-role" value={form.role} onChange={(e) => set('role', e.target.value)} className={inputClass}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {t(r.label)}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] text-[#64748b]">{t(roleHint)}</p>
          </div>

          {form.role === 'clinic' && (
            <div>
              <label htmlFor="nu-clinic" className="block text-xs font-medium text-[#64748b] mb-1.5">
                {t('Clinic name')} {star}
              </label>
              <select id="nu-clinic" value={form.clinic} onChange={(e) => set('clinic', e.target.value)} className={inputClass}>
                <option value="">{t('Select a clinic...')}</option>
                {offered.map((c) => (
                  <option key={String(c._id)} value={String(c._id)}>
                    {c.name}
                  </option>
                ))}
              </select>
              {offered.length === 0 && (
                <p className="mt-1.5 text-[11px] text-[#de524c]">
                  {t('No clinics to choose from. Run Sync from QuickBooks on the Clinics tab first.')}
                </p>
              )}
            </div>
          )}

          {form.role === 'sales_rep' && (
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs font-medium text-[#64748b]">{t('Clinics they can see')}</span>
                <span className="text-[11px] text-[#64748b]">
                  {t('{n} of {total} selected', { n: form.clinics.length, total: offered.length })}
                </span>
              </div>
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('Filter clinics...')}
                aria-label={t('Filter clinics')}
                className="mb-2 h-[32px] w-full border border-[#d6dce8] rounded-[8px] px-3 text-[12px] text-[#0f172a]"
              />
              <div className="border border-[#e2e8f0] rounded-[8px] p-3 max-h-[200px] overflow-y-auto">
                {shownClinics.length === 0 ? (
                  <p className="text-[12px] text-[#64748b]">
                    {needle ? t('Nothing matches that.') : t('No clinics yet. Run Sync from QuickBooks on the Clinics tab first.')}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {shownClinics.map((c) => {
                      const id = String(c._id);
                      return (
                        <label key={id} className="flex items-start gap-2 text-[12px] text-[#0f172a] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.clinics.includes(id)}
                            onChange={() => toggleClinic(id)}
                            className="mt-0.5 w-4 h-4 rounded border-[#d6dce8] text-[#0089ff] focus:ring-[#0089ff]"
                          />
                          <span>{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="nu-email" className="block text-xs font-medium text-[#64748b] mb-1.5">
              {t('Email')} {star}
            </label>
            <input id="nu-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} autoComplete="off" />
          </div>

          <div>
            <label htmlFor="nu-password" className="block text-xs font-medium text-[#64748b] mb-1.5">
              {t('Password')} {star}
            </label>
            <div className="relative">
              <input
                id="nu-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                className={`${inputClass} pr-16`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-[#0089ff]"
              >
                {showPassword ? t('Hide') : t('Show')}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-[#94a3b8]">
              {t('12+ characters with an uppercase letter, a lowercase letter, a number and one of @$!%*?&#')}
            </p>
          </div>

          {!!error && (
            <p className="text-[12px] text-red-600" role="alert">
              {t(error)}
            </p>
          )}
          {confirmNoClinics && (
            <p className="text-[12px] text-[#b45309] bg-[#fffbeb] border border-[#fde68a] rounded-[8px] px-3 py-2" role="alert">
              {t('No clinics are ticked. This rep will sign in and see only their own submissions until an admin gives them clinics. Click Create user again to save it that way.')}
            </p>
          )}
        </form>

        <div className="border-t border-[#e2e8f0] px-5 py-3 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-[40px] px-4 border border-[#e2e8f0] rounded-[10px] text-sm font-semibold text-[#64748b] hover:bg-gray-50 disabled:opacity-50"
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="h-[40px] px-5 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] disabled:opacity-50"
          >
            {saving ? t('Creating...') : t('Create user')}
          </button>
        </div>
      </div>
    </div>
  );
}
